import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { URL_QUERY_PARAMS } from 'shared/constants/queryParams'
import { SingleSelectSearchController } from '../types'
import { useQueryParams2 } from 'shared/hooks/use-query-params'

interface UseSingleSelectTableSearchArgs {
  queryParam: string
  debounceMs?: number
}

export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export const useSingleSelectTableSearch = ({
  queryParam,
  debounceMs = 600,
}: UseSingleSelectTableSearchArgs): SingleSelectSearchController => {
  const location = useLocation()
  const { getParam, setParamsMerged } = useQueryParams2()

  const initialValue = getParam(queryParam) || ''

  const [value, setValue] = useState(initialValue)
  const [active, setActive] = useState(Boolean(initialValue))

  const debouncedValue = useDebounce(value, debounceMs)

  useEffect(() => {
    const currentValue = getParam(queryParam) || ''

    if (currentValue === debouncedValue) return

    setParamsMerged({
      [queryParam]: debouncedValue.trim() ? debouncedValue : null,
      [URL_QUERY_PARAMS.PAGE]: null,
      [URL_QUERY_PARAMS.START_CURSOR]: null,
      [URL_QUERY_PARAMS.END_CURSOR]: null,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const valueFromUrl = params.get(queryParam) || ''

    setValue(current => (current === valueFromUrl ? current : valueFromUrl))

    if (valueFromUrl) {
      setActive(true)
    }
  }, [location.search, queryParam])

  const toggle = useCallback(
    (action?: 'toggle' | 'off', callback?: () => void) => {
      setActive(current => {
        const next = action === 'off' ? false : !current

        if (next && callback) {
          setTimeout(callback, 0)
        }

        return next
      })
    },
    []
  )

  const clear = useCallback(() => {
    setValue('')
    setActive(false)

    setParamsMerged({
      [queryParam]: null,
      [URL_QUERY_PARAMS.PAGE]: null,
      [URL_QUERY_PARAMS.START_CURSOR]: null,
      [URL_QUERY_PARAMS.END_CURSOR]: null,
    })
  }, [queryParam, setParamsMerged])

  return {
    value,
    active,
    debouncedValue,
    setValue,
    toggle,
    clear,
  }
}
